import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { FAIRFLEET_SERVICES, PageLink } from "@/constants/groupConfig";
import { useAddInApplications } from "@/hooks/addins/useAddInApplications";
import { useCreateOrUpdateAddInApplication } from "@/hooks/addins/useCreateOrUpdateAddInApplication";
import { useAddOrUpdateGroup } from "@/hooks/groups/useAddOrUpdateGroup";
import { useGroupByName } from "@/hooks/groups/useGroupByName";
import sha256 from "@/utilities/sha256";

interface Props {
  setErrorMessage: (error: string) => void;
  groupConfig: PageLink;
}

export default function GroupListItem({ groupConfig, setErrorMessage }: Props) {
  const { groupName, addInUrl, name } = groupConfig;
  const { t } = useTranslation();
  const { data: group } = useGroupByName(groupName);
  const { data: addins = [] } = useAddInApplications();
  const { geotabClient, database } = useGeotabClient();
  const { data: parentGroup, isLoading } = useGroupByName(FAIRFLEET_SERVICES);
  const createOrUpdateGroup = useAddOrUpdateGroup();
  const createOrUpdateAddIn = useCreateOrUpdateAddInApplication();
  const hasAddIn = addins.find(
    (addIn: { configuration: { name: string } }) =>
      addIn.configuration?.name === "Fairfleet Services - " + name,
  );

  const [computedHash, setComputedHash] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndHashDatabase = async () => {
      try {
        const hash = await sha256(database);
        setComputedHash(hash);
      } catch {
        setErrorMessage(t("admin.licenses.hashError"));
      }
    };
    fetchAndHashDatabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database]);

  const isLicensed = !!(group && computedHash && group.comments === computedHash);

  const handleLicense = async () => {
    if (!computedHash) {
      setErrorMessage(t("admin.licenses.updateError"));
      return;
    }
    const method = group ? "Set" : "Add";
    const comment = isLicensed ? "" : computedHash;

    if (parentGroup) {
      createOrUpdateGroup.mutate({
        geotabClient,
        name: groupName,
        comment,
        method,
        parentId: parentGroup.id,
        groupId: group?.id,
      });
    } else {
      setErrorMessage(t("admin.licenses.parentNotFound"));
    }
  };

  const handleInstall = async () => {
    const method = hasAddIn ? "Remove" : "Add";
    createOrUpdateAddIn.mutate({
      geotabClient,
      name: name,
      method: method,
      url: addInUrl,
      id: hasAddIn?.id,
    });
  };
  if (isLoading) return null;

  return (
    <ListItem divider>
      <ListItemText
        primary={"Fairfleet Services - " + name + "    (Group: " + groupName + ")"}
        secondary={
          group
            ? group.comments
              ? `${t("admin.licenses.comment")}: ${group.comments}`
              : t("admin.licenses.noComment")
            : t("admin.licenses.groupNotFound")
        }
      />
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        <Button
          variant="contained"
          size="small"
          color={hasAddIn ? "error" : "success"}
          onClick={() => handleInstall()}
          sx={{ minWidth: 90 }}
        >
          {hasAddIn ? t("admin.licenses.remove") : t("admin.licenses.install")}
        </Button>

        <Button
          variant="contained"
          size="small"
          color={isLicensed ? "error" : "success"}
          onClick={() => handleLicense()}
          sx={{ minWidth: 90 }}
        >
          {isLicensed ? t("admin.licenses.unlicense") : t("admin.licenses.license")}
        </Button>
      </Box>
    </ListItem>
  );
}
